import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ChangePasswordOverlay from "../components/auth/ChangePasswordOverlay";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchCurrentUser, updateProfile } from "../store/auth/authSlice";
import { API_BASE_URL, getAuthHeader } from "../api/client";

const tabs = [
  { id: "public", label: "Public Profile" },
  { id: "billing", label: "Billings & Plans" },
  { id: "grocery", label: "My Grocery Lists" },
];

function Settings({ onOpenAuth, onOpenMenu }) {
  const [activeTab, setActiveTab] = useState("public");
  const [groceryView, setGroceryView] = useState("lists"); // lists | create | detail
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    if (activeTab === "public" && isLoggedIn && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [activeTab, isLoggedIn, user, dispatch]);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 text-slate-900">
        <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
        <main className="mx-auto flex max-w-6xl flex-1 items-center justify-center px-4 py-10 md:px-6 min-h-[calc(100vh-120px)]">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <svg
                viewBox="0 0 24 24"
                className="h-8 w-8 text-slate-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
                <path d="M12 14a5 5 0 0 0-5 5v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2a5 5 0 0 0-5-5h-2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900">
              Sign in to access your profile
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Manage your account, address, and preferences after signing in.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => onOpenAuth("login")}
                className="rounded-lg border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => onOpenAuth("signup")}
                className="rounded-lg bg-black px-6 py-2.5 text-sm font-semibold text-white"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-6xl gap-10 px-4 py-10 md:px-6 min-h-[calc(100vh-120px)]">
        <aside className="w-56 rounded-2xl border border-slate-200 bg-white px-5 py-6 text-xs">
          <h2 className="mb-6 text-[18px] font-semibold text-slate-900">
            Settings
          </h2>

          <nav className="space-y-2 text-[13px]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === "grocery") {
                    setGroceryView("lists");
                  }
                }}
                className={`flex w-full items-center rounded-full px-4 py-2 text-left transition ${
                  activeTab === tab.id
                    ? "bg-slate-900 text-white"
                    : "bg-transparent text-slate-600 hover:bg-slate-50"
                }`}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <section className="flex-1 space-y-6">
          {activeTab === "public" && (
            <PublicProfilePanel onOpenChangePassword={() => setChangePasswordOpen(true)} />
          )}
          {activeTab === "billing" && <BillingPlansPanel />}
          {activeTab === "grocery" && (
            <GrocerySettingsPanel
              view={groceryView}
              onChangeView={setGroceryView}
            />
          )}
        </section>
      </main>

      <Footer />

      <ChangePasswordOverlay
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}

function PanelShell({ title, action, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-7 py-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {action}
      </div>
      {children}
    </div>
  );
}

function PublicProfilePanel({ onOpenChangePassword }) {
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
      setAddressLine1(user.address_line1 || "");
      setAddressLine2(user.address_line2 || "");
      setCity(user.city || "");
      setState(user.state || "");
      setPostalCode(user.postal_code || "");
      setCountry(user.country || "");
    }
  }, [user]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  async function handleSave() {
    setSaveError("");
    setSaveLoading(true);
    try {
      await dispatch(
        updateProfile({
          full_name: fullName.trim(),
          address_line1: addressLine1.trim() || null,
          address_line2: addressLine2.trim() || null,
          city: city.trim() || null,
          state: state.trim() || null,
          postal_code: postalCode.trim() || null,
          country: country.trim() || null,
        })
      ).unwrap();
    } catch (err) {
      setSaveError(typeof err === "string" ? err : "Failed to update profile.");
    } finally {
      setSaveLoading(false);
    }
  }

  async function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError("");
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "POST",
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to update profile image.";
        setImageError(msg);
      } else {
        dispatch(fetchCurrentUser());
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
    } finally {
      setImageLoading(false);
      if (e.target) e.target.value = "";
    }
  }

  async function handleDeleteImage() {
    setImageError("");
    setImageLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "DELETE",
        headers: {
          ...getAuthHeader(),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to delete profile image.";
        setImageError(msg);
      } else {
        dispatch(fetchCurrentUser());
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
    } finally {
      setImageLoading(false);
    }
  }

  return (
    <PanelShell
      title="Public Profile"
      action={
        <button
          type="button"
          onClick={onOpenChangePassword}
          className="rounded-full bg-indigo-600 px-5 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
        >
          Change Password
        </button>
      }
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-3">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500 overflow-hidden">
            {user?.image_url ? (
              <img
                src={
                  user.image_url.startsWith("http")
                    ? user.image_url
                    : `${API_BASE_URL.replace(/\/$/, "")}${user.image_url}`
                }
                alt={user.full_name || "Profile"}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>IMG</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
          <div className="space-y-2 text-xs">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageLoading}
              className="block rounded-md border border-slate-300 bg-slate-900 px-4 py-1.5 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {imageLoading ? "Uploading..." : "Change picture"}
            </button>
            <button
              type="button"
              onClick={handleDeleteImage}
              disabled={imageLoading}
              className="block rounded-md border border-slate-300 px-4 py-1.5 text-[11px] font-semibold text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Delete picture
            </button>
            {imageError && (
              <p className="mt-1 max-w-xs text-[11px] text-red-600">
                {imageError}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Full Name
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Full Name"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Email
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Email"
              />
            </div>
            <div className="space-y-1 text-xs md:col-span-2">
              <label className="block text-[12px] font-medium text-slate-600">
                Address Line 1
              </label>
              <input
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Address Line 1"
              />
            </div>
            <div className="space-y-1 text-xs md:col-span-2">
              <label className="block text-[12px] font-medium text-slate-600">
                Address Line 2
              </label>
              <input
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Address Line 2"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                City
              </label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="City"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                State
              </label>
              <input
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="State"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Postal Code
              </label>
              <input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Postal Code"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Country
              </label>
              <input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                placeholder="Country"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {saveError && (
          <p className="text-xs text-red-600">{saveError}</p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saveLoading}
          className="rounded-md bg-indigo-600 px-8 py-2 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saveLoading ? "Saving…" : "Save"}
        </button>
      </div>
    </PanelShell>
  );
}

function BillingPlansPanel() {
  return (
    <PanelShell
      title="Billings & Plans"
      action={
      <></>
      }
    >
      <div className="space-y-6 text-xs text-slate-600">
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
              F
            </div>
            <div>
              <div className="flex items-center gap-3">
              <p className="text-[13px] font-semibold text-slate-900">
                Standard Plan
              </p>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-600">
            Active
          </span>
          </div>
              <p className="mt-0.5 text-[11px] text-slate-600">
                Billing Monthly | Next Payment on 02/03/2026 for £10.75
              </p>
            </div>
          </div>
          <button className="rounded-md bg-slate-900 px-5 py-2 text-xs font-semibold text-white">
          Change Plan
        </button>
        </div>

        <div>
          <p className="mb-3 text-[12px] font-semibold text-slate-700">
            Transaction history
          </p>
          <div className="overflow-hidden rounded-xl border border-slate-200 text-[12px]">
            <div className="grid grid-cols-5 bg-slate-50 px-4 py-2 font-semibold text-slate-600">
              <span>Reference</span>
              <span>Product</span>
              <span>Status</span>
              <span>Date</span>
              <span className="text-right">Amount</span>
            </div>
            {["#95687"].map((id) => (
              <div
                key={id}
                className="grid grid-cols-5 items-center border-t border-slate-100 px-4 py-2 text-slate-600"
              >
                <span>{id}</span>
                <span>Standard</span>
                <span className="text-emerald-500">Paid</span>
                <span>02/02/2026</span>
                <span className="text-right">£29.99</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

function GrocerySettingsPanel({ view, onChangeView }) {
  if (view === "detail") {
    return (
      <GroceryDetail onBack={() => onChangeView("lists")} />
    );
  }

  if (view === "create") {
    return (
      <GroceryListsWithCreate
        onCancel={() => onChangeView("lists")}
        onCreate={() => onChangeView("detail")}
      />
    );
  }

  return (
    <GroceryListsOverview
      onOpenCreate={() => onChangeView("create")}
      onOpenDetail={() => onChangeView("detail")}
    />
  );
}

function GroceryListsOverview({ onOpenCreate, onOpenDetail }) {
  return (
    <PanelShell
      title="My Grocery Lists"
      action={
        <button
          className="rounded-md bg-slate-900 px-5 py-2 text-xs font-semibold text-white"
          onClick={onOpenCreate}
        >
          + Create List
        </button>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {["List Title 1", "List Title 2", "List Title 3"].map((name, index) => (
          <button
            key={name}
            type="button"
            onClick={onOpenDetail}
            className="flex flex-col items-stretch rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left hover:border-indigo-500"
          >
            <div className="mb-3 h-24 rounded-xl bg-slate-100" />
            <p className="text-xs font-semibold text-slate-800">
              {name}
            </p>
            <div className="flex items-center justify-between">
            <p className="mt-1 text-[11px] text-slate-500">
            Budget- £50
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
           Products-4
            </p>
            </div>
          </button>
        ))}
      </div>
    </PanelShell>
  );
}

function GroceryListsWithCreate({ onCancel, onCreate }) {
  return (
    <PanelShell
      title="My Grocery Lists"
      action={
        <button className="rounded-md bg-slate-900 px-5 py-2 text-xs font-semibold text-white">
          + Create List
        </button>
      }
    >
      <div className="relative">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {["List title 1", "List title 2"].map((name) => (
            <div
              key={name}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
            >
              <div className="mb-3 flex items-center justify-between rounded-xl bg-slate-100 px-4 py-6">
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-10 w-10 rounded-md bg-slate-200"
                    />
                  ))}
                </div>
              </div>
              <p className="text-xs font-semibold text-slate-800">{name}</p>
              <p className="mt-1 text-[11px] text-slate-500">Budget: £50</p>
              <p className="mt-1 text-[11px] text-slate-500">Products: 4</p>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-black/5">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-5 text-xs shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-[14px] font-semibold text-slate-900">
                Create List
              </p>
              <button
                type="button"
                onClick={onCancel}
                className="text-slate-500 hover:text-slate-800"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <input
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                  placeholder="Title"
                />
              </div>
              <div className="space-y-1">
                <input
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-indigo-500"
                  placeholder="Budget"
                />
              </div>

              <div className="mt-4 flex items-center justify-center gap-3">
               
                <button
                  type="button"
                  onClick={onCreate}
                  className="rounded-md bg-slate-900 px-8 py-2 text-sm font-semibold text-white"
                >
                  Save →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

function GroceryDetail({ onBack }) {
  return (
    <PanelShell
      title="Grocery Detail"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600"
          >
            Back to lists
          </button>
          <button className="rounded-md border border-slate-200 px-4 py-1.5 text-xs font-medium text-white bg-black">
            Rename
          </button>
          <button className="rounded-md bg-[#FF383C] px-4 py-1.5 text-xs font-semibold text-white">
            Delete
          </button>
        </div>
      }
    >
      <div className="mb-4 text-xs text-slate-600">
        <p className="text-[13px] font-semibold text-slate-800">List 01</p>
        <p className="mt-0.5 text-[11px] text-slate-500">
          3 items · Estimated £9.49
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((idx) => (
          <div
            key={idx}
            className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-3"
          >
            <div className="mb-3 h-24 rounded-xl bg-slate-100" />
            <p className="text-xs font-semibold text-slate-800">
              Easy Wing Turbo
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              3 items · from Tesco
            </p>
            <div className="mt-3 flex items-center justify-between text-[11px]">
              <span className="font-semibold text-slate-800">£3.49</span>
              <button className="rounded-full bg-indigo-600 px-3 py-1 text-[11px] font-semibold text-white">
                View
              </button>
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
}

export default Settings;

